export type FxShape = 'dot' | 'spark' | 'puff'

export type FxBurst = {
  n: number
  speed: number
  life: number
  size: number
  shape?: FxShape
  gravity?: number
  drag?: number
  w?: number
  h?: number
  x?: number
  y?: number
  colors?: readonly string[]
}

export type FlipStep = readonly [ms: number, bursts: readonly FxBurst[]]

export type FlipVariant = {
  readonly key: string
  readonly flipAt: number
  readonly doneAt: number
  readonly fx: readonly FlipStep[]
}

const EMBERS = ['#ff8a3d', '#ffb35c', '#ffd36e', '#fff2c4']
const GLITTER = ['#ff6fb5', '#ffd36e', '#7dffb2', '#6fd2ff', '#b18cff', '#ffffff']

export const FLIP_VARIANTS: readonly FlipVariant[] = [
  { key: 'crescent', flipAt: 180, doneAt: 1350, fx: [[900, [{ n: 16, shape: 'spark', speed: 150, life: 0.9, size: 7, w: 150, h: 260 }]]] },
  {
    key: 'dust',
    flipAt: 380,
    doneAt: 1300,
    fx: [
      [
        640,
        [
          { n: 120, shape: 'dot', speed: 300, life: 1.5, size: 2.6, gravity: 260 },
          { n: 18, shape: 'spark', speed: 200, life: 1.1, size: 8 },
        ],
      ],
    ],
  },
  {
    key: 'constellation',
    flipAt: 1050,
    doneAt: 2050,
    fx: [[1350, [{ n: 30, shape: 'spark', speed: 120, life: 1.2, size: 8, w: 160, h: 280 }]]],
  },
  { key: 'flash', flipAt: 240, doneAt: 1000, fx: [[300, [{ n: 46, shape: 'spark', speed: 460, life: 0.8, size: 7 }]]] },
  { key: 'orbits', flipAt: 650, doneAt: 1750, fx: [[1300, [{ n: 20, shape: 'dot', speed: 110, life: 1, size: 2.4, w: 200, h: 200 }]]] },
  { key: 'arch', flipAt: 150, doneAt: 1350, fx: [] },
  {
    key: 'sunrise',
    flipAt: 450,
    doneAt: 2000,
    fx: [
      [700, [{ n: 50, shape: 'dot', speed: 60, life: 2.4, size: 2.4, gravity: -70, w: 170, h: 200, drag: 0.99 }]],
      [1300, [{ n: 30, shape: 'dot', speed: 50, life: 2, size: 2, gravity: -60, w: 170, h: 260, drag: 0.99 }]],
    ],
  },
  {
    key: 'holo',
    flipAt: 120,
    doneAt: 1450,
    fx: [[620, [{ n: 70, shape: 'spark', speed: 260, life: 1.3, size: 6, gravity: 120, colors: GLITTER }]]],
  },
  {
    key: 'mist',
    flipAt: 200,
    doneAt: 1800,
    fx: [
      [220, [{ n: 16, shape: 'puff', speed: 70, life: 2, size: 50, w: 120, h: 200 }]],
      [900, [{ n: 10, shape: 'puff', speed: 50, life: 1.8, size: 40, w: 160, h: 260 }]],
    ],
  },
  {
    key: 'legend',
    flipAt: 800,
    doneAt: 1800,
    fx: [
      [
        790,
        [
          { n: 110, shape: 'dot', speed: 420, life: 1.4, size: 2.8, gravity: 180, colors: EMBERS },
          { n: 24, shape: 'spark', speed: 300, life: 1, size: 9, colors: ['#ffd36e', '#fff2c4'] },
        ],
      ],
    ],
  },
]

export const GLOW_COUNT = 11
export const DISMISS_COUNT = 10

export function randomIndex(count: number): number {
  return Math.floor(Math.random() * count)
}

export function scaleBurst(b: FxBurst, k: number): FxBurst {
  return { ...b, speed: b.speed * k, w: (b.w ?? 40) * k, h: (b.h ?? 60) * k, size: b.shape === 'puff' ? b.size * k : b.size }
}
